import { validatedDisplayView } from "../i18n/display-view.js";
import { portalText, portalLocale } from "../i18n/portal.js";
/**
 * Turning `/v1/conversations`' wire shapes into what the History and Live tabs render.
 *
 * It sits beside `api-gateway.ts` rather than inside it because both the real gateway and
 * the suites that check the decoding read it, and because this is where the console's copy
 * for a conversation lives — the words a caller-hidden row shows, how an event kind reads in
 * a timeline, what "Today 14:02" means. Copy, never configuration: nothing here has a
 * value that a deployment could get wrong.
 *
 * **No field is invented.** A missing or mistyped field raises `MalformedResponseError`
 * rather than becoming a zero or an empty string, because a documented response that arrives
 * incomplete is a server defect and a console that renders `0` for it reports the defect as
 * a fact.
 */
import { money, OUTBOUND_ORIGIN, TALK_RATE_KIND, type CurrencyCode, type Money, type OutboundOrigin, type TalkRateKind } from "@wetalk/domain";

import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readRecord,
  readText,
  readWholeNumber,
} from "./decode.js";
import { formatDuration, formatMoney } from "../money.js";
import { talkTimeText } from "../talk-time.js";
import { hasTakenMessageOfWire } from "./taken-message-view.js";
// plan-029 WAPP-HIST2: caller confirmation's events in words.
import { partyConfirmationEventSentenceOf } from "./party-confirmation-view.js";
// plan-029 WAPP-HIST1: caller protection's events, end reasons and History chips.
import {
  isPartyProtectionEndReason,
  partyProtectionEventSentenceOf,
  partyProtectionHistoryWireOf,
  partyProtectionReasonText,
} from "./party-protection-view.js";
import type {
  ChannelLabel,
  ConversationCustomerOrderView,
  ConversationEventView,
  ConversationSegmentView,
  HistoryChannelLabelFilter,
  HistoryOutcomeFilter,
  HistoryStatView,
  Medium,
  MediumSplitView,
  PastConversationRowView,
  RecordingReferenceView,
  TranscriptLineView,
  WebChatConversationRowView,
} from "./gateway.js";

/** What a Member and a Viewer see where the caller's number would be. */
export const CALLER_HIDDEN = () => (portalText("shell", "caller_hidden_82bbb5d8"));

/** What a row's outcome tag reads when the conversation reached none. */
export const NO_OUTCOME = () => (portalText("shell", "no_outcome_a4fe5d40"));

const MEDIUM: readonly Medium[] = ["voice", "web_chat", "whatsapp", "viber"];

const SPEAKER: readonly TranscriptLineView["speaker"][] = ["assistant", "caller", "system"];

/**
 * How each `conversation_event.kind` reads in the timeline.
 *
 * The API sends the kind and a structured `detail`; the sentence is the console's, which is
 * why the wire carries neither. A kind this console was built before renders as itself
 * rather than as nothing — a new kind is data, not a migration, so the map is open.
 *
 * A `Map` built from pairs rather than an object literal, because several of the kinds are
 * `ConversationEventKind` VALUES that contain glossary-banned segments (`integration_call`).
 * As object keys they would be declared identifiers and the lint would — correctly — read
 * them; as string literals in a list they are what they are: the wire's own vocabulary,
 * quoted.
 */
const EVENT_COPY = () => (new Map<string, string>([
  ["sip_invite", portalText("shell", "sip_invite_received_4e7e68ff")],
  ["answered", portalText("shell", "conversation_answered_18d24648")],
  ["menu_lookup", portalText("shell", "menu_lookup_2aac0144")],
  ["integration_call", portalText("shell", "connection_operation_sent_43a418d1")],
  ["integration_call_blocked", portalText("shell", "connection_operation_blocked_not_on_the_allowlist_80a621e8")],
  ["transfer", portalText("shell", "transferred_f76d1e2a")],
  // plan-015 FWD (design §28.5.7.6): a forward's three moments, should they ever arrive as kinds of their own (the
  // runtime writes them today as `transfer` with the code in the detail — see `forwardSentenceOf`).
  ["forward_started", portalText("shell", "forwarding_to_a_phone_dcf56398")],
  ["forward_answered", portalText("shell", "forward_answered_ee56384e")],
  ["forward_ended", portalText("shell", "forward_ended_60d5649c")],
  ["language_switch", portalText("shell", "language_switched_df500e36")],
  ["language_unavailable", portalText("shell", "language_unavailable_continued_in_the_current_one_b9d06414")],
  ["barge_in", portalText("shell", "caller_interrupted_59e0fbf9")],
  ["consent_granted", portalText("shell", "recording_consent_granted_f558b77f")],
  ["consent_refused", portalText("shell", "recording_consent_refused_a6c4906c")],
  ["refusal_detected", portalText("shell", "do_not_call_phrase_detected_9ee8b614")],
  ["suppression_written", portalText("shell", "added_to_the_do_not_call_list_de84b2a1")],
  ["recording_unavailable", portalText("shell", "recording_unavailable_d1720e38")],
  ["runtime_error", portalText("shell", "runtime_error_50c894b7")],
  ["outcome", portalText("shell", "outcome_recorded_9d02d012")],
  ["hangup", portalText("shell", "hung_up_add9ea21")],
  // plan-031 VMD-WAPP (design §42.9): detection's one verdict per outbound conversation — see `voicemailSentenceOf`.
  ["voicemail_detection", portalText("shell", "voicemail.event_kind")],
]));

/**
 * How the Live board words "how far it has got", from the most recent STAGE event kind.
 *
 * Review #101: the API now skips the runtime's internal outcomes (the speaker lock, presence
 * checks, unanswered questions), so an `outcome` that reaches here is an order placed, the
 * goodbye after it, or a presence check nobody answered — the conversation really is ending.
 */
const PHASE_COPY = () => (new Map<string, string>([
  ["sip_invite", "connecting"],
  ["answered", portalText("shell", "greeting_the_caller_e411d5f6")],
  ["menu_lookup", portalText("shell", "looking_something_up_c7b284a8")],
  ["integration_call", portalText("shell", "talking_to_your_system_49bfcdbd")],
  ["transfer", "transferring"],
  ["forward_started", portalText("shell", "forwarding_to_a_phone_a944dbae")],
  ["forward_answered", "forwarded"],
  ["language_switch", portalText("shell", "switching_language_81a09810")],
  ["barge_in", "listening"],
  ["outcome", portalText("shell", "wrapping_up_cbf3d339")],
  ["hangup", "ending"],
]));

/** Before the first stage the conversation is simply under way, and saying so is honest. */
export const PHASE_UNKNOWN = () => (portalText("shell", "in_conversation_e6886d90"));

export function eventCopyOf(kind: string): string {
  return EVENT_COPY().get(kind) ?? kind;
}

/** A kind this console does not word is not a stage it can name: it reads as under way. */
export function phaseOf(lastEventKind: string | null): string {
  if (lastEventKind === null) return PHASE_UNKNOWN();
  return PHASE_COPY().get(lastEventKind) ?? PHASE_UNKNOWN();
}

/** A language code in words — `el` → "Greek" — or the code itself when the runtime has no name. */
export function languageNameOf(code: string): string {
  try {
    const name = new Intl.DisplayNames([portalLocale()], { type: "language" }).of(code);
    return name === undefined || name === "" ? code : name;
  } catch {
    return code;
  }
}

function textField(detail: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = detail[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function wholeField(detail: Readonly<Record<string, unknown>>, key: string): number | null {
  const value = detail[key];
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

/** Why a conversation ended, from `hangup.detail.reason`, in plain words. */
const HANGUP_COPY = () => (new Map<string, string>([
  ["caller_hangup", portalText("shell", "the_caller_hung_up_bf39096d")],
  ["assistant_hangup", portalText("shell", "the_agent_ended_the_call_01998854")],
  // plan-022 (design §34): the agent ended a finished conversation itself (`end_conversation`).
  ["voice_agent_ended", portalText("shell", "the_agent_ended_the_conversation_a9ef70e9")],
  ["transferred", portalText("shell", "handed_over_to_a_person_e6b25e5b")],
  ["timeout", portalText("shell", "ended_after_a_long_silence_f6498a04")],
  ["error", portalText("shell", "ended_by_an_error_d29f1e08")],
  // plan-031 VMD-WAPP (design §42.9): a voicemail answered and the runtime hung up at once, without a word.
  [VOICEMAIL_END_REASON, portalText("shell", "voicemail.hangup")],
]));

/**
 * plan-031 VMD-WAPP (design §42.10, §42.11; glossary §6.46 *Voicemail*): the end reason of an outbound conversation a
 * voicemail answered and the runtime hung up on at once. History's chip asks for it by end reason, like caller
 * protection's; it is a code, never a template's outcome word.
 */
export const VOICEMAIL_END_REASON = "voicemail";

/** "Voicemail" / «Τηλεφωνητής» — the end reason (and the campaign outcome) in one word. */
export function voicemailReasonText(): string {
  return portalText("shell", "voicemail.outcome");
}

/** Each voicemail signal (`VoicemailSignal`) in words, as the `voicemail_detection` event lists what was found. */
const VOICEMAIL_SIGNAL_COPY = () => (new Map<string, string>([
  ["beep", portalText("shell", "voicemail.signal.beep")],
  ["announcement_phrase", portalText("shell", "voicemail.signal.announcement_phrase")],
  ["voicemail_phrase", portalText("shell", "voicemail.signal.voicemail_phrase")],
  ["voicemail_word", portalText("shell", "voicemail.signal.voicemail_word")],
  ["long_speech", portalText("shell", "voicemail.signal.long_speech")],
  ["person_phrase", portalText("shell", "voicemail.signal.person_phrase")],
  ["short_answer", portalText("shell", "voicemail.signal.short_answer")],
  ["silence", portalText("shell", "voicemail.signal.silence")],
]));

/**
 * One `voicemail_detection` event in plain words (design §42.9, §42.11): "Voicemail detected: a beep", «Εντοπίστηκε
 * τηλεφωνητής: ήχος». The detail holds codes and milliseconds only — never what was heard — so nothing here can quote
 * a greeting. `shadow` says it was not hung up on (detection only listened); `unsure` says it was treated as a person
 * (owner decision DEC-3); `late` says it was found after the greeting had started. An unknown signal is left out rather
 * than shown raw.
 */
export function voicemailSentenceOf(detail: Readonly<Record<string, unknown>>): string {
  const rawSignal = detail["signal"];
  const signal = (Array.isArray(rawSignal) ? rawSignal : [])
    .map((one) => (typeof one === "string" ? VOICEMAIL_SIGNAL_COPY().get(one) : undefined))
    .filter((one): one is string => one !== undefined)
    .join(", ");
  const found = signal === "" ? null : signal;
  let head: string;
  switch (textField(detail, "verdict")) {
    case "voicemail":
      if (detail["mode"] === "shadow") {
        head = found === null
          ? portalText("shell", "voicemail.event_shadow")
          : portalText("shell", "voicemail.event_shadow_signal", { signal: found });
      } else {
        head = found === null
          ? portalText("shell", "voicemail.event_detected")
          : portalText("shell", "voicemail.event_detected_signal", { signal: found });
      }
      break;
    case "person":
      head = found === null
        ? portalText("shell", "voicemail.event_person")
        : portalText("shell", "voicemail.event_person_signal", { signal: found });
      break;
    case "unsure":
      return portalText("shell", "voicemail.event_unsure");
    default:
      return eventCopyOf("voicemail_detection");
  }
  return detail["stage"] === "late" ? `${head} · ${portalText("shell", "voicemail.stage_late")}` : head;
}

/**
 * One timeline entry in plain words, from its kind AND its detail (review #85).
 *
 * "Outcome recorded" six times told an owner nothing; the detail says which question the agent
 * could not answer, which order was placed, whether the caller was still there. The caller's own
 * words appear only where the API sent them — a Viewer's events arrive without them (review
 * #133), and the sentence then names what happened without quoting.
 */
export function eventSentenceOf(kind: string, detailValue: unknown): string {
  const detail: Readonly<Record<string, unknown>> =
    typeof detailValue === "object" && detailValue !== null && !Array.isArray(detailValue)
      ? (detailValue as Record<string, unknown>)
      : {};
  if (kind === "outcome") {
    const outcomeKind = textField(detail, "kind");
    const spoken = textField(detail, "text");
    switch (outcomeKind) {
      case "unanswered_question":
        return spoken === null
          ? portalText("shell", "the_agent_could_not_answer_a_question_9fbabe6c")
          : portalText("shell", "the_agent_could_not_answer_value_e95503ff", { v0: String(spoken) });
      case "customer_order_placed": {
        const reference = textField(detail, "reference");
        return reference === null ? portalText("shell", "order_placed_bb5d68da") : portalText("shell", "order_value_placed_93f82a35", { v0: String(reference) });
      }
      case "customer_order_confirmed": {
        const lines = wholeField(detail, "lineCount");
        return lines === null
          ? portalText("shell", "the_caller_confirmed_the_order_3a64fb02")
          : portalText("shell", "orders.confirmed", { count: lines, figure: String(lines) });
      }
      case "customer_order_not_placed": {
        const problem = textField(detail, "problem");
        return problem === null ? portalText("shell", "the_order_could_not_be_placed_239ab5c7") : portalText("shell", "the_order_could_not_be_placed_value_5419c5de", { v0: String(problem) });
      }
      case "speaker_lock":
        return portalText("shell", "the_agent_locked_on_to_the_caller_s_voice_d683d051");
      case "speaker_ignored":
        return spoken === null
          ? portalText("shell", "a_second_voice_in_the_background_was_ignored_7b12e28b")
          : portalText("shell", "a_second_voice_in_the_background_was_ignored_value_4d522bb6", { v0: String(spoken) });
      case "presence_check":
        return portalText("shell", "the_agent_asked_whether_the_caller_was_still_there_a75ee46f");
      case "presence_no_response":
        return portalText("shell", "no_answer_to_are_you_still_there_the_agent_ended_the_call_7f346930");
      case "goodbye_after_customer_order":
        return portalText("shell", "the_agent_said_goodbye_after_the_order_d33752f6");
      // plan-022 (design §34): the agent ends a finished conversation itself; the reason is a code, never shown raw.
      case "voice_agent_ending_conversation":
        switch (textField(detail, "reason")) {
          case "caller_said_goodbye":
            return portalText("shell", "the_agent_ended_the_conversation_the_caller_said_goodbye_c38f380a");
          case "caller_needs_nothing_else":
            return portalText("shell", "the_agent_ended_the_conversation_the_caller_needed_nothing_else_28c1fb2e");
          case "work_done_caller_agreed":
            return portalText("shell", "the_agent_ended_the_conversation_the_work_was_done_and_the_caller_agreed_b7f0cdb8");
          default:
            return portalText("shell", "the_agent_ended_the_conversation_a9ef70e9");
        }
      case "voice_agent_ending_cancelled":
        return portalText("shell", "the_caller_spoke_again_so_the_agent_did_not_end_the_conversation_d4f647fa");
      case "voice_agent_ending_refused":
        return portalText("shell", "the_agent_tried_to_end_the_conversation_too_early_and_carried_on_c2c7441b");
      case null:
        return portalText("shell", "outcome_recorded_9d02d012");
      default:
        return portalText("shell", "outcome_recorded_value_b0f23a7b", { v0: String(outcomeKind.replaceAll("_", " ")) });
    }
  }
  if (kind === "hangup") {
    const reason = textField(detail, "reason");
    if (isPartyProtectionEndReason(reason)) return partyProtectionReasonText(reason);
    return reason === null ? portalText("shell", "hung_up_add9ea21") : (HANGUP_COPY().get(reason) ?? `Hung up (${reason.replaceAll("_", " ")})`);
  }
  // plan-029 WAPP-HIST1 (design §40.12.2): a refusal, an end, the final minute — codes only, never a number.
  if (kind === "party_protection") return partyProtectionEventSentenceOf(detail);
  // plan-029 WAPP-HIST2 (design §40.12.2): caller confirmation — codes and counts only, never a code or a number.
  if (kind === "party_confirmation") return partyConfirmationEventSentenceOf(detail);
  // plan-031 VMD-WAPP (design §42.9): the verdict and its signals in words — codes only arrive, never a word heard.
  if (kind === "voicemail_detection") return voicemailSentenceOf(detail);
  if (kind === "language_switch") {
    const from = textField(detail, "from");
    const to = textField(detail, "to");
    return from !== null && to !== null
      ? portalText("shell", "language_switched_from_value_to_value_e45b772c", { v0: String(languageNameOf(from)), v1: String(languageNameOf(to)) })
      : portalText("shell", "language_switched_df500e36");
  }
  if (kind === "language_unavailable") {
    const requested = textField(detail, "requested");
    return requested === null
      ? portalText("shell", "language_unavailable_continued_in_the_current_one_b9d06414")
      : portalText("shell", "value_is_not_available_continued_in_the_current_language_b52e046f", { v0: String(languageNameOf(requested)) });
  }
  if (kind === "runtime_error") {
    const code = textField(detail, "code");
    return code === null ? portalText("shell", "runtime_error_50c894b7") : portalText("shell", "runtime_error_value_c23dc5cc", { v0: String(code.replaceAll("_", " ")) });
  }
  if (kind === "integration_call" || kind === "integration_call_blocked") {
    const operation = textField(detail, "operationId");
    return operation === null ? eventCopyOf(kind) : `${eventCopyOf(kind)}: ${operation}`;
  }
  if (kind === "barge_in") return portalText("shell", "the_caller_interrupted_the_agent_c8a7654f");
  if (kind === "transfer" || kind === "forward_started" || kind === "forward_answered" || kind === "forward_ended") {
    return forwardSentenceOf(kind, detail);
  }
  return eventCopyOf(kind);
}

/** "9 s", "4 min 10 s" — a forward's durations as History states them. */
export function forwardDurationOf(ms: number): string {
  const second = Math.max(0, Math.round(ms / 1000));
  const minute = Math.floor(second / 60);
  const rest = second % 60;
  if (minute === 0) return portalText("shell", "value_s_09263e7c", { v0: String(String(rest)) });
  return rest === 0 ? portalText("shell", "value_min_7504a6f3", { v0: String(String(minute)) }) : portalText("shell", "value_min_value_s_7a6246d3", { v0: String(String(minute)), v1: String(String(rest)) });
}

/** Why a forward ended without being put through, in plain words. */
const FORWARD_OUTCOME_COPY = () => (new Map<string, string>([
  ["no_answer", portalText("shell", "nobody_answered_3e1a0fef")],
  ["busy", portalText("shell", "the_line_was_busy_66a43e81")],
  ["unreachable", portalText("shell", "the_number_could_not_be_reached_3989b51c")],
  ["refused", portalText("shell", "the_call_was_declined_07e782cb")],
  ["failed", portalText("shell", "it_could_not_be_connected_2d32ca6d")],
  ["caller_left", portalText("shell", "the_caller_hung_up_while_it_rang_7cbf6e1b")],
]));

/** Why a forward was not tried at all (the runtime's `forward_not_attempted` reasons), in plain words. */
const FORWARD_NOT_ATTEMPTED_COPY = () => (new Map<string, string>([
  ["forward_switch_off", portalText("shell", "forwarding_is_not_switched_on_yet_93a4f6df")],
  ["not_a_carrier_leg", portalText("shell", "this_call_did_not_come_in_on_a_wetalk_number_7c7784ec")],
  ["forward_destination_refused", portalText("shell", "the_number_cannot_be_forwarded_to_cfc302f8")],
  ["forward_destination_unverifiable", portalText("shell", "the_number_could_not_be_checked_461e0a58")],
  ["caller_id_unavailable", portalText("shell", "there_was_no_wetalk_number_to_show_aadb7437")],
  ["forward_rate_limited", portalText("shell", "too_many_calls_were_forwarded_this_hour_7d082727")],
  ["forward_rate_unreadable", portalText("shell", "the_hourly_limit_could_not_be_checked_36611a0e")],
]));

/**
 * A forward's moments in History (plan-015 FWD, design §28.5.7.6): "Forwarded to +30***0012 · answered after 9 s",
 * "Forwarded call · 4 min 10 s", "Forward to +30***0012: nobody answered — a message was offered". The number is the
 * runtime's masked form (`forward_to_masked`), never the number itself.
 */
function forwardSentenceOf(kind: string, detail: Readonly<Record<string, unknown>>): string {
  const code = kind === "transfer" ? textField(detail, "code") : kind;
  const to = textField(detail, "forward_to_masked");
  const target = to === null ? portalText("shell", "a_phone_1bce3987") : to;
  switch (code) {
    case "forward_started": {
      const ring = wholeField(detail, "ring_second");
      return ring === null ? portalText("shell", "forwarding_to_value_581c5d6b", { v0: String(target) }) : portalText("shell", "forwarding_to_value_rings_up_to_value_s_62da6120", { v0: String(target), v1: String(String(ring)) });
    }
    case "forward_answered": {
      const after = wholeField(detail, "answered_after_ms");
      return after === null ? portalText("shell", "forwarded_to_value_c2c24ad5", { v0: String(target) }) : portalText("shell", "forwarded_to_value_answered_after_value_40945e0a", { v0: String(target), v1: String(forwardDurationOf(after)) });
    }
    case "forward_ended": {
      const outcome = textField(detail, "outcome");
      if (outcome === "bridged") {
        const forwarded = wholeField(detail, "forwarded_ms");
        return forwarded === null ? portalText("shell", "forwarded_call_ended_8c7ead4b") : portalText("shell", "forwarded_call_value_ccabde93", { v0: String(forwardDurationOf(forwarded)) });
      }
      const why = outcome === null ? null : (FORWARD_OUTCOME_COPY().get(outcome) ?? outcome.replaceAll("_", " "));
      return why === null ? portalText("shell", "forward_to_value_ended_fe22b653", { v0: String(target) }) : portalText("shell", "forward_to_value_value_a_message_was_offered_512c20ac", { v0: String(target), v1: String(why) });
    }
    case "forward_not_attempted": {
      const reason = textField(detail, "reason");
      const why = reason === null ? null : (FORWARD_NOT_ATTEMPTED_COPY().get(reason) ?? reason.replaceAll("_", " "));
      return why === null ? portalText("shell", "not_forwarded_a_message_was_offered_955fbc6a") : portalText("shell", "not_forwarded_value_a_message_was_offered_e0465e8c", { v0: String(why) });
    }
    case "overflow_transferred":
      return portalText("shell", "put_through_to_a_phone_1edc9cc6");
    default:
      return eventCopyOf(kind);
  }
}

/** What a website visitor is called where a phone number would be. Never the visitor key. */
export const WEBSITE_VISITOR = () => (portalText("shell", "website_visitor_59351c74"));

/** The prefix the runtime writes before a visitor key (`apps/runtime/src/chat/ledger.ts`). */
const WEB_PARTY_PREFIX = "web:";

/**
 * The caller's number, or the words that say why it is not there.
 *
 * A web party's address is `web:` + the visitor key, and the key is never printed (plan-004 WC-90):
 * a caller of this that has no channel label — the Live board — still reads "Website visitor".
 */
export function partyLabel(partyAddress: string | null): string {
  if (partyAddress === null) return CALLER_HIDDEN();
  return partyAddress.startsWith(WEB_PARTY_PREFIX) ? WEBSITE_VISITOR() : partyAddress;
}

const CHANNEL_LABEL: readonly ChannelLabel[] = [
  "phone",
  "browser_test",
  "website_voice",
  "web_chat",
  "web_chat_with_voice",
  "test_chat",
  "whatsapp",
  "viber",
];

/**
 * Each channel label in the owner's words. A `Map` from pairs, like {@link EVENT_COPY}: two of the
 * labels contain the glossary-banned segment `test`, and as object keys they would be identifiers.
 * The task's words: web chat is "Website chat", so it never reads as the console's own chat. A
 * conversation typed AND spoken is "Web chat + voice" — plan-005 D9's words, verbatim.
 */
const CHANNEL_LABEL_WORD = () => (new Map<ChannelLabel, string>([
  ["phone", portalText("shell", "phone_77064d52")],
  ["browser_test", portalText("shell", "browser_test_54fdcc6a")],
  ["website_voice", portalText("shell", "website_voice_b8d49602")],
  ["web_chat", portalText("shell", "website_chat_772d1329")],
  ["web_chat_with_voice", portalText("shell", "web_chat_voice_718a8f95")],
  ["test_chat", portalText("shell", "test_chat_c85a2cea")],
  ["whatsapp", "WhatsApp"],
  ["viber", portalText("shell", "viber_70774829")],
]));

export function channelLabelWordOf(label: ChannelLabel): string {
  return CHANNEL_LABEL_WORD().get(label) ?? label;
}

export function readChannelLabel(value: unknown, path: string): ChannelLabel {
  const raw = readText(value, path);
  const found = CHANNEL_LABEL.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a channel label", raw);
  return found;
}

/** The labels a website visitor arrives by — who is called a visitor, and who has a tag. */
export function isWebsiteChannelLabel(label: ChannelLabel): boolean {
  return label === "web_chat" || label === "website_voice" || label === "web_chat_with_voice";
}

/**
 * A widget conversation the visitor both typed and spoke in (plan-005 D9): one row, and in its detail
 * every line is marked spoken or typed and every switch is shown.
 */
export function isMixedChannelLabel(label: ChannelLabel): boolean {
  return label === "web_chat_with_voice";
}

/** A conversation typed rather than spoken: it has turns and never a recording. */
export function isTextChannelLabel(label: ChannelLabel): boolean {
  return label === "web_chat" || label === "test_chat" || label === "whatsapp" || label === "viber";
}

/**
 * Who the far end was, in the words a row prints (plan-004 WC-90): a phone number (or "Caller
 * hidden"), a website visitor with the tag that tells them apart ("Visitor 8F22A1"), or — for the
 * owner's own tests — what the test was. Never a visitor key: a web address is not printed even
 * where the reader may see it.
 */
export function partyLabelOf(
  one: Pick<PastConversationRowView, "partyAddress" | "channelLabel" | "visitorTag">,
): string {
  switch (one.channelLabel) {
    case "browser_test":
      return portalText("shell", "browser_test_54fdcc6a");
    case "test_chat":
      return portalText("shell", "test_chat_c85a2cea");
    case "web_chat":
    case "website_voice":
    case "web_chat_with_voice":
      return one.visitorTag === null ? WEBSITE_VISITOR() : portalText("shell", "visitor_value_814fbb2d", { v0: String(one.visitorTag) });
    case "phone":
    case "whatsapp":
    case "viber":
      return partyLabel(one.partyAddress);
  }
}

/** The transcript's word for the far end: a caller on the phone, a visitor on the website, you in a test. */
export function farEndWordOf(label: ChannelLabel): string {
  if (isWebsiteChannelLabel(label)) return portalText("shell", "visitor_c909c1c7");
  if (label === "browser_test" || label === "test_chat") return portalText("shell", "you_905cb326");
  return portalText("shell", "caller_3fda6d67");
}

/** History's channel chip as a `/v1/conversations` query parameter. "All" sends nothing. */
export function historyChannelLabelWireOf(label: HistoryChannelLabelFilter): Record<string, string> {
  return label === "all" ? {} : { channel_label: label };
}

/** The outcome tag's text. */
export function outcomeLabel(outcome: string | null): string {
  return outcome === null ? NO_OUTCOME() : outcome;
}

export function readMoney(value: unknown, path: string): Money {
  const one = readRecord(value, path);
  const currency = readText(one["currency"], `${path}.currency`);
  return money(readWholeNumber(one["amount_minor"], `${path}.amount_minor`), currency as CurrencyCode);
}

export function readNullableText(value: unknown, path: string): string | null {
  if (value === null) return null;
  return readText(value, path);
}

export function readMedium(value: unknown, path: string): Medium {
  const raw = readText(value, path);
  const found = MEDIUM.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a medium", raw);
  return found;
}

/**
 * WAPP-ENDCALL (2026-10-28): a live row's `outbound_origin` — `null` for an inbound conversation. Omitted reads as
 * inbound (an api from before the field offers no "End call", never a guessed one); a value outside migration 0048's
 * set is a server defect and raises.
 */
export function readOutboundOrigin(row: Readonly<Record<string, unknown>>, path: string): OutboundOrigin | null {
  const raw = "outbound_origin" in row ? row["outbound_origin"] : null;
  if (raw === null) return null;
  const text = readText(raw, path);
  const found = OUTBOUND_ORIGIN.find((one) => one === text);
  if (found === undefined) throw new MalformedResponseError(path, OUTBOUND_ORIGIN.join(" | "), text);
  return found;
}

function readDirection(value: unknown, path: string): "inbound" | "outbound" {
  const raw = readText(value, path);
  if (raw !== "inbound" && raw !== "outbound") {
    throw new MalformedResponseError(path, "inbound or outbound", raw);
  }
  return raw;
}

export function readSpeaker(value: unknown, path: string): TranscriptLineView["speaker"] {
  const raw = readText(value, path);
  const found = SPEAKER.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a speaker label", raw);
  return found;
}

/**
 * `Today 14:02`, `Yesterday 20:14`, `Tomorrow 09:00`, `18 Sep 14:02`.
 *
 * `now` is an argument rather than a `Date.now()` inside, so a suite can assert the words
 * without owning the clock, and so a page left open overnight is not quietly wrong.
 */
export function formatWhen(isoTimestamp: string, now: Date): string {
  const at = new Date(isoTimestamp);
  if (Number.isNaN(at.getTime())) {
    throw new MalformedResponseError("started_at", "an RFC 3339 timestamp", isoTimestamp);
  }
  const clock = `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
  const dayOf = (value: Date): number =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const daysApart = Math.round((dayOf(now) - dayOf(at)) / 86_400_000);
  if (daysApart === 0) return portalText("shell", "today_value_98ab9355", { v0: String(clock) });
  if (daysApart === 1) return portalText("shell", "yesterday_value_7b96d8f6", { v0: String(clock) });
  // WAPP-DASH (2026-10-28): a campaign starting or ending tomorrow reads "Tomorrow 14:00", not a bare date.
  if (daysApart === -1) return portalText("shell", "tomorrow_value", { v0: String(clock) });
  return `${new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { day: "numeric", month: "short" }).format(at)} ${clock}`;
}

/** `03:30:12`, in the browser's zone: one entry of a single conversation's timeline. */
export function formatClock(isoTimestamp: string): string {
  const at = new Date(isoTimestamp);
  if (Number.isNaN(at.getTime())) {
    throw new MalformedResponseError("occurred_at", "an RFC 3339 timestamp", isoTimestamp);
  }
  const two = (value: number): string => String(value).padStart(2, "0");
  return `${two(at.getHours())}:${two(at.getMinutes())}:${two(at.getSeconds())}`;
}

/** Whole seconds between two instants, floored. Negative is impossible and reads as zero. */
export function elapsedSecondsOf(isoStartedAt: string, now: Date): number {
  const at = new Date(isoStartedAt).getTime();
  return Math.max(0, Math.floor((now.getTime() - at) / 1000));
}

/** One row of `GET /v1/conversations`, as the History list renders it. */
export function rowOfWire(
  value: unknown,
  path: string,
  now: Date,
): PastConversationRowView {
  const one = readRecord(value, path);
  const hasTakenMessage = hasTakenMessageOfWire(one["has_taken_message"], `${path}.has_taken_message`);
  return validatedDisplayView({
    conversationId: readText(one["conversation_id"], `${path}.conversation_id`),
    partyAddress: readNullableText(one["party_address"], `${path}.party_address`),
    medium: readMedium(one["medium"], `${path}.medium`),
    channelLabel: readChannelLabel(one["channel_label"], `${path}.channel_label`),
    visitorTag: readNullableText(one["visitor_tag"], `${path}.visitor_tag`),
    partyConversationCount: readWholeNumber(one["party_conversation_count"], `${path}.party_conversation_count`),
    outcome: readNullableText(one["outcome"], `${path}.outcome`),
    endReason: readText(one["end_reason"], `${path}.end_reason`),
    direction: readDirection(one["direction"], `${path}.direction`),
    languageCode: readNullableText(one["language_code"], `${path}.language_code`),
    get whenText() { return formatWhen(readText(one["started_at"], `${path}.started_at`), now); },
    startedAt: readText(one["started_at"], `${path}.started_at`),
    billedSeconds: one["billed_second"] === null
      ? 0
      : readWholeNumber(one["billed_second"], `${path}.billed_second`),
    billedAmount: readMoney(one["billed_amount"], `${path}.billed_amount`),
    chargedSeconds: readWholeNumber(one["charged_second"], `${path}.charged_second`),
    listAmount: readMoney(one["list_amount"], `${path}.list_amount`),
    agentVersionLabel: readNullableText(
      one["agent_version_label"],
      `${path}.agent_version_label`,
    ),
    isTest: readBoolean(one["is_test"], `${path}.is_test`),
    // plan-017 READ-B: the kind and rate it is priced at, and whether it has no price (required fields).
    talkRateKind: readNullableTalkRateKind(one["talk_rate_kind"], `${path}.talk_rate_kind`),
    talkRatePerMinute:
      one["talk_rate_minor_per_minute"] === null
        ? null
        : money(
            readWholeNumber(one["talk_rate_minor_per_minute"], `${path}.talk_rate_minor_per_minute`),
            readMoney(one["billed_amount"], `${path}.billed_amount`).currency,
          ),
    priceUnavailable: readBoolean(one["price_unavailable"], `${path}.price_unavailable`),
    // plan-018 API-MSG: a caller left a message in it (absent from an api before plan-018).
    ...(hasTakenMessage === undefined ? {} : { hasTakenMessage }),
  });
}

/** A `TalkRateKind` (plan-017); anything else is a malformed answer, never a guess. */
export function readTalkRateKind(value: unknown, path: string): TalkRateKind {
  const raw = readText(value, path);
  const found = TALK_RATE_KIND.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a talk rate kind", raw);
  return found;
}

/** A `TalkRateKind` or `null` (plan-017). */
export function readNullableTalkRateKind(value: unknown, path: string): TalkRateKind | null {
  return value === null ? null : readTalkRateKind(value, path);
}

export function recordingOfWire(
  value: unknown,
  path: string,
): RecordingReferenceView | null {
  if (value === null || value === undefined) return null;
  const one = readRecord(value, path);
  return {
    recordingId: readText(one["recording_id"], `${path}.recording_id`),
    blobContainer: readText(one["blob_container"], `${path}.blob_container`),
    blobName: readText(one["blob_name"], `${path}.blob_name`),
    durationSeconds: Math.round(
      readWholeNumber(one["duration_ms"], `${path}.duration_ms`) / 1000,
    ),
    byteSize: readWholeNumber(one["byte_size"], `${path}.byte_size`),
    codec: readText(one["codec"], `${path}.codec`),
  };
}

export function transcriptOfWire(value: unknown, path: string): readonly TranscriptLineView[] {
  return readArray(value, path).map((entry, index) => {
    const one = readRecord(entry, `${path}[${String(index)}]`);
    const at = `${path}[${String(index)}]`;
    return {
      speaker: readSpeaker(one["speaker"], `${at}.speaker`),
      text: readText(one["text"], `${at}.text`),
      // A stored turn is final by definition: `turn.partial` is a stream frame, and a row in
      // `turn` is what was said, not what the recogniser was still revising.
      partial: false,
      ...(typeof one["turn_index"] === "number" ? { turnIndex: one["turn_index"] } : {}),
      // plan-005 OP-44: an api from before OP-44 sends neither; one that sends them sends them right.
      ...("mode" in one ? { mode: readTurnMode(one["mode"], `${at}.mode`) } : {}),
      ...("started_at" in one ? { startedAt: readText(one["started_at"], `${at}.started_at`) } : {}),
    };
  });
}

function readTurnMode(value: unknown, path: string): "spoken" | "typed" {
  const raw = readText(value, path);
  if (raw !== "spoken" && raw !== "typed") throw new MalformedResponseError(path, "spoken or typed", raw);
  return raw;
}

// ---------------------------------------------------------------------------------------
// A mixed conversation (plan-005 OP-44, D9)
// ---------------------------------------------------------------------------------------

/**
 * A mixed conversation's parts, off `getConversation`'s `segment`. The API sends the field on every
 * conversation since OP-44 (empty for a single-mode one); an api from before it sends none, and the
 * panel then reads the conversation as single-mode — `undefined`, never an invented empty list.
 */
export function segmentOfWire(
  body: Readonly<Record<string, unknown>>,
  path: string,
): readonly ConversationSegmentView[] | undefined {
  if (!("segment" in body)) return undefined;
  return readArray(body["segment"], `${path}.segment`).map((entry, index) => {
    const at = `${path}.segment[${index}]`;
    const one = readRecord(entry, at);
    const medium = readText(one["medium"], `${at}.medium`);
    if (medium !== "voice" && medium !== "web_chat") {
      throw new MalformedResponseError(`${at}.medium`, "voice or web_chat", medium);
    }
    const talk = one["talk_ms"];
    return {
      ordinal: readWholeNumber(one["ordinal"], `${at}.ordinal`),
      medium,
      startedAt: readText(one["started_at"], `${at}.started_at`),
      talkSeconds: talk === null ? null : Math.round(readWholeNumber(talk, `${at}.talk_ms`) / 1000),
      recording: recordingOfWire(one["recording"], `${at}.recording`),
    };
  });
}

/** A mixed conversation's per-medium seconds and charge, off `medium_split`; `undefined` from an older api. */
export function mediumSplitOfWire(
  body: Readonly<Record<string, unknown>>,
  path: string,
): MediumSplitView | null | undefined {
  if (!("medium_split" in body)) return undefined;
  const value = body["medium_split"];
  if (value === null) return null;
  const at = `${path}.medium_split`;
  const one = readRecord(value, at);
  return {
    voiceSeconds: readWholeNumber(one["voice_second"], `${at}.voice_second`),
    textSeconds: readWholeNumber(one["text_second"], `${at}.text_second`),
    voiceChargedSeconds: readWholeNumber(one["voice_charged_second"], `${at}.voice_charged_second`),
    textChargedSeconds: readWholeNumber(one["text_charged_second"], `${at}.text_charged_second`),
    voiceAmount: readMoney(one["voice_amount"], `${at}.voice_amount`),
    textAmount: readMoney(one["text_amount"], `${at}.text_amount`),
  };
}

/** The word a line of a mixed conversation is marked with: how it was said. */
export function lineModeWordOf(mode: "spoken" | "typed"): string {
  return mode === "spoken" ? portalText("shell", "voice_3091c844") : portalText("shell", "text_c3328c39");
}

/**
 * A switch marker in a mixed conversation's transcript, with its time and the part's talk time:
 * "Started in text · 1:00 of talk", "Switched to voice · 2:30 of talk", "Switched to text" while a part
 * is still open. The time itself is printed beside it, as a timeline entry's is.
 */
export function segmentMarkerTextOf(segment: Pick<ConversationSegmentView, "ordinal" | "medium" | "talkSeconds">): string {
  const mode = segment.medium === "voice" ? "voice" : "text";
  const lead = segment.ordinal === 1 ? portalText("shell", "started_in_value_49c2982c", { v0: String(mode) }) : portalText("shell", "switched_to_value_a441ad82", { v0: String(mode) });
  return segment.talkSeconds === null ? lead : portalText("shell", "value_value_of_talk_eb914940", { v0: String(lead), v1: String(formatDuration(segment.talkSeconds)) });
}

/**
 * The per-medium line under a mixed conversation's billed figure (OP-24's rule): "Voice 2:30 · €0.42 ·
 * Text 1:30 · €0.75". The two amounts add up to the row's charge; the trial covers the voice seconds
 * first, so the voice part is the one that reads cheaper while the free minutes last.
 */
export function mediumSplitTextOf(split: MediumSplitView): string {
  return (
    portalText("shell", "voice_value_value_text_value_value_788b854e", { v0: String(formatDuration(split.voiceSeconds)), v1: String(formatMoney(split.voiceAmount)), v2: String(formatDuration(split.textSeconds)), v3: String(formatMoney(split.textAmount)) })
  );
}

/**
 * plan-018 INT (2026-10-14; RUN's note): the support engine's engineering facts — `information_retrieved` (passage
 * ordinals and timings, one per caller turn) and `prefix_changed` (two hashes) — are not something that happened in the
 * conversation an owner could act on, so History's timeline leaves them out. The API still serves them (ids, numbers
 * and hashes only), and the operator console's own detail is unchanged.
 */
export const TIMELINE_HIDDEN_KIND: ReadonlySet<string> = new Set(["information_retrieved", "prefix_changed"]);

export function timelineOfWire(
  value: unknown,
  path: string,
  now: Date,
): readonly ConversationEventView[] {
  return readArray(value, path).flatMap((entry, index) => {
    const one = readRecord(entry, `${path}[${String(index)}]`);
    // The panel's header already says which day; a timeline of one call reads by the second.
    void now;
    const kind = readText(one["kind"], `${path}[${index}].kind`);
    if (TIMELINE_HIDDEN_KIND.has(kind)) return [];
    return [
      validatedDisplayView({
        get atText() { return formatClock(readText(one["occurred_at"], `${path}[${index}].occurred_at`)); },
        what: eventSentenceOf(kind, one["detail"]),
      }),
    ];
  });
}

/** The window's facts, as History's tiles need them. */
export interface HistoryFigure {
  readonly conversationCount: number;
  readonly answeredCount: number;
  readonly billedSecondTotal: number;
  /** What billing charges: the free trial covers the seconds first (review #109). */
  readonly billedAmount: Money;
  /** The same seconds at the list rate, whoever pays for them. */
  readonly listAmount: Money;
  /** The seconds billing charges for. Fewer than `billedSecondTotal` while the trial covers some. */
  readonly chargedSecondTotal: number;
  /** plan-017: conversations of the set with billed seconds and no price, left out of both amounts. */
  readonly priceUnavailableCount: number;
}

/**
 * "2 conversations have no price and are left out" — said beside a figure that left some out (plan-017 READ-B: a
 * conversation with no price is never given a guessed one), or `null` when none was.
 */
export function priceUnavailableNoteOf(count: number): string | null {
  return count === 0 ? null : portalText("operations", "history.price_unavailable_left_out", { count });
}

/**
 * The words under a money figure that the trial covered in whole or in part (review #109):
 * "€8.97 at list price, covered by your free minutes". `null` when nothing was covered — the
 * figure is then exactly the list price and needs no note.
 */
export function trialNoteOf(billedSecond: number, chargedSecond: number, listAmount: Money): string | null {
  if (chargedSecond >= billedSecond || billedSecond === 0) return null;
  if (chargedSecond === 0) return portalText("shell", "value_at_list_price_covered_by_your_free_minutes_6a9122c2", { v0: String(formatMoney(listAmount)) });
  return portalText("shell", "value_at_list_price_your_free_minutes_covered_part_of_it_da02f9dd", { v0: String(formatMoney(listAmount)) });
}

/**
 * History's four tiles, as the design draws them: Calls shown, Average length, Minutes billed,
 * Cost. The average is over ANSWERED conversations — one that rang out has no length — and a
 * figure of nothing is "—", not zero. The answered share, which the design does not print, is
 * the first tile's `note`; that only talk time is billed is the last one's.
 */
export function historyStatOf(figure: HistoryFigure): readonly HistoryStatView[] {
  return [
    validatedDisplayView({
      get label() { return portalText("shell", "calls_shown_4d31e642"); },
      value: String(figure.conversationCount),
      get note() { return figure.conversationCount === 0
          ? null
          : portalText("shell", "value_answered_ef20116c", { v0: String(String(Math.round((figure.answeredCount / figure.conversationCount) * 100))) }); },
    }),
    validatedDisplayView({
      get label() { return portalText("shell", "average_length_2df99da9"); },
      value:
        figure.answeredCount === 0
          ? "—"
          : formatDuration(Math.round(figure.billedSecondTotal / figure.answeredCount)),
      get note() { return portalText("shell", "over_answered_conversations_only_8e9a4af1"); },
    }),
    validatedDisplayView({
      get label() { return portalText("shell", "minutes_billed_4b250ccd"); },
      value: talkTimeText(figure.billedSecondTotal),
      get note() { return portalText("shell", "billed_by_the_second_9276bea0"); },
    }),
    validatedDisplayView({
      get label() { return portalText("shell", "cost_64ae43e8"); },
      value: formatMoney(figure.billedAmount),
      note:
        priceUnavailableNoteOf(figure.priceUnavailableCount) ??
        trialNoteOf(figure.billedSecondTotal, figure.chargedSecondTotal, figure.listAmount) ??
        "Billed talk time only — ringing and queueing are free.",
    }),
  ];
}

/**
 * The History tab's outcome chip, as `/v1/conversations` query parameters (review #113, #122).
 *
 * The four fixed chips are the API's `outcome_state`; any other value is one outcome exactly as
 * the agent_template words it, sent as `outcome`. Two words from before the chips were renamed
 * still arrive in old links: `completed` meant any outcome, `abandoned` meant none.
 */
export function historyOutcomeWireOf(outcome: HistoryOutcomeFilter): Record<string, string> {
  // plan-029 WAPP-HIST1: caller protection's chips ask by end reason (`end_reason`), never by an outcome word.
  const partyProtection = partyProtectionHistoryWireOf(outcome);
  if (partyProtection !== null) return partyProtection;
  // plan-031 VMD-WAPP (design §42.11): the voicemail chip asks by end reason too (VMD-API's History filter).
  if (outcome === VOICEMAIL_END_REASON) return { outcome_state: "any", end_reason: VOICEMAIL_END_REASON };
  switch (outcome) {
    case "all":
      return { outcome_state: "any" };
    case "reached":
    case "completed":
      return { outcome_state: "reached" };
    case "transferred":
      return { outcome_state: "transferred" };
    case "none":
    case "abandoned":
      return { outcome_state: "unreached" };
    default:
      return { outcome_state: "any", outcome };
  }
}

/** The order a conversation placed, or `null`. Raises on a malformed one rather than guessing. */
export function customerOrderOfWire(value: unknown, path: string): ConversationCustomerOrderView | null {
  if (value === null) return null;
  const one = readRecord(value, path);
  return {
    reference: readText(one["reference"], `${path}.reference`),
    total: readMoney(one["total"], `${path}.total`),
    status: readText(one["order_status"], `${path}.order_status`),
  };
}

/**
 * One row of `GET /v1/messaging/conversations` — the Messaging page's conversation table (plan-004
 * WC-90, design §17.10.7). Every field the table prints or links by is required: a row that arrived
 * without its label or its voice agent would be a server defect, not an empty cell.
 */
export function messagingConversationRowOfWire(
  value: unknown,
  path: string,
  now: Date,
): WebChatConversationRowView {
  const one = readRecord(value, path);
  const billed = one["billed_second"];
  return validatedDisplayView({
    conversationId: readText(one["conversation_id"], `${path}.conversation_id`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    voiceAgentName: readText(one["voice_agent_name"], `${path}.voice_agent_name`),
    medium: readMedium(one["medium"], `${path}.medium`),
    channelLabel: readChannelLabel(one["channel_label"], `${path}.channel_label`),
    partyAddress: readNullableText(one["party_address"], `${path}.party_address`),
    visitorTag: readNullableText(one["visitor_tag"], `${path}.visitor_tag`),
    partyConversationCount: readWholeNumber(one["party_conversation_count"], `${path}.party_conversation_count`),
    lastMessage: readNullableText(one["last_message"], `${path}.last_message`),
    transcriptWithheld: readBoolean(one["transcript_withheld"], `${path}.transcript_withheld`),
    languageCode: readNullableText(one["language_code"] ?? null, `${path}.language_code`),
    outcome: readNullableText(one["outcome"] ?? null, `${path}.outcome`),
    endReason: readText(one["end_reason"], `${path}.end_reason`),
    get whenText() { return formatWhen(readText(one["started_at"], `${path}.started_at`), now); },
    billedSeconds:
      billed === null || billed === undefined ? null : readWholeNumber(billed, `${path}.billed_second`),
    isTest: readBoolean(one["is_test"], `${path}.is_test`),
  });
}

/** `channel_label_option` off a History page: the labels in scope, most frequent first. */
export function channelLabelOptionOfWire(value: unknown, path: string): readonly ChannelLabel[] {
  return readArray(value, path).map((one, index) => readChannelLabel(one, `${path}[${String(index)}]`));
}
